import type { DayId } from '../calendar.ts'
import { addDays, diffDays } from '../calendar.ts'
import type { DomainEvent, Moment } from '../events.ts'
import type { MemberId, StepId, TaskId } from '../ids.ts'
import type { FamilyState } from '../state.ts'

/**
 * Where a child actually gets stuck.
 *
 * "It is not working for him" is not something you can act on. This turns the
 * log into the four questions that are: does he start it, does he finish it,
 * which step does he stop at, and at what time of day does any of it happen.
 *
 * It reads the raw log rather than the current state, because the interesting
 * part is the shape of the attempts, including the ones that went nowhere.
 */

export interface StickingPoint {
  readonly stepId: StepId
  readonly text: string
  /** Days the attempt reached this step and stopped there. */
  readonly times: number
  /** Where in the list it sits, counting from one. */
  readonly position: number
}

export interface TaskInsight {
  readonly taskId: TaskId
  readonly name: string
  readonly kind: 'routine' | 'mission'
  readonly moment: Moment | undefined
  readonly stepCount: number
  /** Days in the window on which at least one step was ticked. */
  readonly daysStarted: number
  /** Days it was marked as done. */
  readonly daysFinished: number
  readonly daysValidated: number
  /** Days it was started and left unfinished. */
  readonly daysAbandoned: number
  /** The step attempts most often stop at. Undefined if nothing was abandoned. */
  readonly stickingPoint: StickingPoint | undefined
  /** Typical minutes from the first step to marking it done. */
  readonly medianMinutesToFinish: number | undefined
  /** Typical local hour the first step happens. */
  readonly typicalHour: number | undefined
}

export interface MemberInsight {
  readonly memberId: MemberId
  readonly name: string
  readonly windowDays: number
  readonly daysWithAnyActivity: number
  readonly afternoonRoutineDays: number
  readonly tasks: readonly TaskInsight[]
}

interface Attempt {
  /** `iso` keeps its offset: the hour the family saw, not the hour in UTC. */
  steps: { id: StepId; at: number; iso: string }[]
  markedAt: number | undefined
  validated: boolean
}

const median = (values: readonly number[]): number | undefined => {
  if (values.length === 0) return undefined
  const sorted = [...values].sort((a, b) => a - b)
  const middle = Math.floor(sorted.length / 2)
  if (sorted.length % 2 === 1) return sorted[middle]
  return Math.round(((sorted[middle - 1] ?? 0) + (sorted[middle] ?? 0)) / 2)
}

/** Local hour of an ISO instant, using the offset the event carries. */
function hourOf(iso: string): number | undefined {
  const match = /T(\d{2}):/.exec(iso)
  return match ? Number(match[1]) : undefined
}

export function insightsFor(
  state: FamilyState,
  events: readonly DomainEvent[],
  memberId: MemberId,
  upTo: DayId,
  windowDays = 14,
): MemberInsight {
  const from = addDays(upTo, -(windowDays - 1))
  const inWindow = (day: DayId): boolean => day >= from && day <= upTo

  // taskId -> day -> attempt
  const attempts = new Map<TaskId, Map<DayId, Attempt>>()
  const activeDays = new Set<DayId>()

  const attemptFor = (taskId: TaskId, day: DayId): Attempt => {
    let byDay = attempts.get(taskId)
    if (!byDay) {
      byDay = new Map()
      attempts.set(taskId, byDay)
    }
    let attempt = byDay.get(day)
    if (!attempt) {
      attempt = { steps: [], markedAt: undefined, validated: false }
      byDay.set(day, attempt)
    }
    return attempt
  }

  for (const event of events) {
    if (!inWindow(event.day)) continue
    switch (event.type) {
      case 'StepCompleted': {
        if (event.payload.memberId !== memberId) break
        activeDays.add(event.day)
        attemptFor(event.payload.taskId, event.day).steps.push({
          id: event.payload.stepId,
          at: Date.parse(event.at),
          iso: event.at,
        })
        break
      }
      case 'StepUncompleted': {
        if (event.payload.memberId !== memberId) break
        const attempt = attemptFor(event.payload.taskId, event.day)
        const index = attempt.steps.findIndex((s) => s.id === event.payload.stepId)
        if (index >= 0) attempt.steps.splice(index, 1)
        break
      }
      case 'TaskMarkedDone': {
        if (event.payload.memberId !== memberId) break
        activeDays.add(event.day)
        attemptFor(event.payload.taskId, event.day).markedAt = Date.parse(event.at)
        break
      }
      case 'TaskReturned': {
        if (event.payload.memberId !== memberId) break
        attemptFor(event.payload.taskId, event.day).markedAt = undefined
        break
      }
      case 'TaskValidated': {
        if (event.payload.memberId !== memberId) break
        attemptFor(event.payload.taskId, event.day).validated = true
        break
      }
      default:
        break
    }
  }

  const tasks: TaskInsight[] = []
  let afternoonRoutineDays = 0

  for (const task of state.tasks.values()) {
    if (task.memberId !== memberId || task.archived) continue
    const byDay = attempts.get(task.id) ?? new Map<DayId, Attempt>()

    let daysStarted = 0
    let daysFinished = 0
    let daysValidated = 0
    const stoppedAt = new Map<StepId, number>()
    const durations: number[] = []
    const hours: number[] = []

    for (const attempt of byDay.values()) {
      if (attempt.steps.length === 0 && attempt.markedAt === undefined) continue
      daysStarted += 1

      const first = attempt.steps[0]
      if (first) {
        // Read off the stamp as written. Round-tripping through Date would
        // answer in UTC, and 20:00 in Madrid would come back as 18:00.
        const hour = hourOf(first.iso)
        if (hour !== undefined) hours.push(hour)
      }

      if (attempt.markedAt !== undefined) {
        daysFinished += 1
        if (first) durations.push(Math.round((attempt.markedAt - first.at) / 60_000))
      } else if (task.steps.length > 0) {
        // Abandoned. The interesting step is the first one it never reached.
        const doneIds = new Set(attempt.steps.map((s) => s.id))
        const blocker = task.steps.find((s) => !doneIds.has(s.id))
        if (blocker) stoppedAt.set(blocker.id, (stoppedAt.get(blocker.id) ?? 0) + 1)
      }
      if (attempt.validated) daysValidated += 1
    }

    if (task.kind === 'routine' && task.moment === 'afternoon') {
      afternoonRoutineDays = daysValidated
    }

    let stickingPoint: StickingPoint | undefined
    let worst = 0
    for (const [stepId, times] of stoppedAt) {
      if (times <= worst) continue
      const position = task.steps.findIndex((s) => s.id === stepId)
      const text = task.steps[position]?.text
      if (text === undefined) continue
      worst = times
      stickingPoint = { stepId, text, times, position: position + 1 }
    }

    tasks.push({
      taskId: task.id,
      name: task.name,
      kind: task.kind,
      moment: task.moment,
      stepCount: task.steps.length,
      daysStarted,
      daysFinished,
      daysValidated,
      daysAbandoned: daysStarted - daysFinished,
      stickingPoint,
      medianMinutesToFinish: median(durations),
      typicalHour: median(hours),
    })
  }

  tasks.sort((a, b) => b.daysAbandoned - a.daysAbandoned || a.name.localeCompare(b.name, 'es'))

  return {
    memberId,
    name: state.members.get(memberId)?.name ?? '',
    windowDays: Math.min(windowDays, diffDays(from, upTo) + 1),
    daysWithAnyActivity: activeDays.size,
    afternoonRoutineDays,
    tasks,
  }
}
